import { createContext, useContext, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthNewContext";

export const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [dailyUsers, setDailyUsers] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [timeSpent, setTimeSpent] = useState([]);
  const [userSessions, setUserSessions] = useState([]); // 🔥 NEW
  const [selectedUser, setSelectedUser] = useState(null);

  const [feedbackStatusLoadingId, setFeedbackStatusLoadingId] = useState(null);
  const [feedbackStatusError, setFeedbackStatusError] = useState(null);

  const [usersStatus, setUsersStatus] = useState([]);
  const [usersStatusLoading, setUsersStatusLoading] = useState(false);

  const { user } = useAuth(); // 🔥 get token

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const getHeaders = () => ({
    Authorization: `Bearer ${user?.token}`,
  });

  const fetchAllSessions = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(
        `${BASE_URL}/api/admin/sessions-latest`, // 🔥 IMPORTANT
        { headers: getHeaders() },
      );

      setSessions(res.data);
    } catch (err) {
      console.error("Admin fetch error:", err);
      setError("Failed to fetch sessions");
    } finally {
      setLoading(false);
    }
  };

  /* ================= SINGLE USER HISTORY ================= */
  const fetchUserSessions = async (userID) => {
    try {
      setLoading(true);
      setSelectedUser(userID);

      const res = await axios.get(`${BASE_URL}/api/admin/user/${userID}`, {
        headers: getHeaders(),
      });
      console.log("user sessions", res.data);
      setUserSessions(res.data);
    } catch (err) {
      console.error("User history error:", err);
      setError("Failed to fetch user history");
    } finally {
      setLoading(false);
    }
  };

  /* ================= CLEAR USER ================= */
  const clearUserSessions = () => {
    setUserSessions([]);
    setSelectedUser(null);
  };
  /* ================= FETCH ALL USER SESSIONS ================= */
  //   const fetchAllSessions = async () => {
  //     try {
  //       setLoading(true);
  //       setError(null);
  //       console.log("TOKEN:", user?.token);

  //       const res = await axios.get(`${BASE_URL}/api/admin/sessions`, {
  //         headers: {
  //           Authorization: `Bearer ${user?.token}`, // 🔥 IMPORTANT
  //         },
  //       });

  //       console.log("ADMIN DATA:", res.data);

  //       setSessions(res.data);
  //     } catch (err) {
  //       console.error("Admin fetch error:", err.response || err);
  //       setError("Failed to fetch sessions");
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  const fetchTotalUsers = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/admin/analytics/total-users`,
        { headers: getHeaders() },
      );

      setTotalUsers(res.data.totalUsers);
    } catch (err) {
      console.error("Total users error:", err);
    }
  };

  /* ================= NEW: ACTIVE USERS ================= */
  const fetchActiveUsers = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/admin/analytics/active-users`,
        { headers: getHeaders() },
      );

      setActiveUsers(res.data.activeUsers);
    } catch (err) {
      console.error("Active users error:", err);
    }
  };

  /* ================= NEW: DAILY USERS ================= */
  const fetchDailyUsers = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/admin/analytics/daily-users`,
        { headers: getHeaders() },
      );

      setDailyUsers(res.data);
    } catch (err) {
      console.error("Daily users error:", err);
    }
  };

  /* ================= NEW: TIME SPENT ================= */
  const fetchTimeSpent = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/admin/analytics/time-spent`,
        { headers: getHeaders() },
      );

      setTimeSpent(res.data);
    } catch (err) {
      console.error("Time spent error:", err);
    }
  };

  /* ================= LOAD ALL ANALYTICS ================= */
  const loadAnalytics = async () => {
    try {
      setLoading(true);

      const results = await Promise.allSettled([
        fetchTotalUsers(),
        fetchActiveUsers(),
        fetchDailyUsers(),
        fetchTimeSpent(),
      ]);

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          console.error(`API ${index} failed:`, result.reason);
        }
      });
    } catch (err) {
      console.error("Analytics load error:", err);
    } finally {
      setLoading(false);
    }
  };
  /* ================= EXPORT CSV ================= */
  const exportUserSessionsCSV = async (userID = null) => {
    try {
      const url = `${BASE_URL}/api/admin/export-user-sessions/csv${
        userID ? `?userID=${encodeURIComponent(userID)}` : ""
      }`;

      const res = await axios.get(url, {
        responseType: "blob",
        headers: getHeaders(),
      });

      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");

      link.href = window.URL.createObjectURL(blob);
      link.download = userID
        ? `${userID}_sessions.csv`
        : "all_users_sessions.csv";

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("CSV export error:", err);
    }
  };

  /* ================= EXPORT EXCEL ================= */
  const exportUserSessionsExcel = async (userID = null) => {
    try {
      const url = `${BASE_URL}/api/admin/export-user-sessions/excel${
        userID ? `?userID=${encodeURIComponent(userID)}` : ""
      }`;

      const res = await axios.get(url, {
        responseType: "blob",
        headers: getHeaders(),
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");

      link.href = window.URL.createObjectURL(blob);
      link.download = userID
        ? `${userID}_sessions.xlsx`
        : "all_users_sessions.xlsx";

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Excel export error:", err);
    }
  };
  const fetchStockInfo = async (symbol) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/stockInfo/${symbol}`, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (err) {
      if (err.response?.data?.showModal) {
        return { trialLimit: true };
      }

      throw err;
    }
  };
  /* ================= FEEDBACKS================= */
  const fetchAllFeedbacks = async () => {
    try {
      setFeedbackLoading(true);
      setFeedbackError(null);

      const res = await axios.get(
        `${BASE_URL}/api/admin/feedbacks`, // 🔥 your protected route
        {
          headers: getHeaders(),
        },
      );

      setFeedbacks(res.data);
    } catch (err) {
      console.error("Feedback fetch error:", err);
      setFeedbackError("Failed to fetch feedbacks");
    } finally {
      setFeedbackLoading(false);
    }
  };
  const updateFeedbackStatus = async (feedbackId, status) => {
    try {
      setFeedbackStatusLoadingId(feedbackId);
      setFeedbackStatusError(null);

      const res = await axios.patch(
        `${BASE_URL}/api/admin/feedbacks/${feedbackId}/status`,
        {
          status,
        },
        {
          headers: getHeaders(),
        },
      );

      const updatedFeedback = res.data?.data;

      if (!updatedFeedback) {
        throw new Error("Updated feedback was not returned");
      }

      setFeedbacks((prevFeedbacks) =>
        prevFeedbacks.map((feedback) =>
          feedback.id === updatedFeedback.id ? updatedFeedback : feedback,
        ),
      );

      return {
        success: true,
        data: updatedFeedback,
        message: res.data?.message || "Feedback status updated",
      };
    } catch (err) {
      console.error("Feedback status update error:", err.response?.data || err);

      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to update feedback status";

      setFeedbackStatusError(message);

      return {
        success: false,
        message,
      };
    } finally {
      setFeedbackStatusLoadingId(null);
    }
  };

  // Al user
  const fetchUsersStatus = async () => {
    try {
      setUsersStatusLoading(true);

      const res = await axios.get(`${BASE_URL}/api/admin/users-status`, {
        headers: getHeaders(),
      });

      setUsersStatus(res.data.data || []);
    } catch (err) {
      console.error("Users status fetch error:", err);
    } finally {
      setUsersStatusLoading(false);
    }
  };

  const updateUserAccountStatus = async (userID, isDeactivate) => {
    try {
      await axios.patch(
        `${BASE_URL}/api/admin/user/${userID}/deactivate`,
        { isDeactivate },
        { headers: getHeaders() },
      );

      setUsersStatus((prev) =>
        prev.map((u) => (u.userID === userID ? { ...u, isDeactivate } : u)),
      );
    } catch (err) {
      console.error("Update user status error:", err);
    }
  };

  return (
    <AdminContext.Provider
      value={{
        sessions,
        loading,
        error,
        fetchAllSessions,
        totalUsers,
        activeUsers,
        dailyUsers,
        timeSpent,
        fetchTotalUsers,
        fetchActiveUsers,
        fetchDailyUsers,
        fetchTimeSpent,
        loadAnalytics,
        fetchUserSessions,
        userSessions,
        clearUserSessions,
        exportUserSessionsCSV,
        exportUserSessionsExcel,
        fetchStockInfo,
        fetchAllFeedbacks,
        feedbacks,
        feedbackLoading,
        feedbackError,
        usersStatus,
        usersStatusLoading,
        fetchUsersStatus,
        updateUserAccountStatus,
        updateFeedbackStatus,
        feedbackStatusLoadingId,
        feedbackStatusError,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
// export const AdminProvider = ({ children }) => {
//   const [sessions, setSessions] = useState([]);

//   // 🔥 NEW STATES
//   const [totalUsers, setTotalUsers] = useState(0);
//   const [activeUsers, setActiveUsers] = useState(0);
//   const [dailyUsers, setDailyUsers] = useState([]);
//   const [timeSpent, setTimeSpent] = useState([]);

//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);

//   const { user } = useAuth();

//   const BASE_URL = process.env.REACT_APP_API_BASE_URL;

//   /* ================= COMMON HEADERS ================= */
//   const getHeaders = () => ({
//     Authorization: `Bearer ${user?.token}`,
//   });

//   /* ================= EXISTING (UNCHANGED) ================= */
//   const fetchAllSessions = async () => {
//     try {
//       setLoading(true);
//       setError(null);

//       const res = await axios.get(`${BASE_URL}/api/admin/sessions`, {
//         headers: getHeaders(),
//       });

//       setSessions(res.data);
//     } catch (err) {
//       console.error("Admin fetch error:", err.response || err);
//       setError("Failed to fetch sessions");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ================= NEW: TOTAL USERS ================= */
//   const fetchTotalUsers = async () => {
//     try {
//       const res = await axios.get(
//         `${BASE_URL}/api/admin/analytics/total-users`,
//         { headers: getHeaders() },
//       );

//       setTotalUsers(res.data.totalUsers);
//     } catch (err) {
//       console.error("Total users error:", err);
//     }
//   };

//   /* ================= NEW: ACTIVE USERS ================= */
//   const fetchActiveUsers = async () => {
//     try {
//       const res = await axios.get(
//         `${BASE_URL}/api/admin/analytics/active-users`,
//         { headers: getHeaders() },
//       );

//       setActiveUsers(res.data.activeUsers);
//     } catch (err) {
//       console.error("Active users error:", err);
//     }
//   };

//   /* ================= NEW: DAILY USERS ================= */
//   const fetchDailyUsers = async () => {
//     try {
//       const res = await axios.get(
//         `${BASE_URL}/api/admin/analytics/daily-users`,
//         { headers: getHeaders() },
//       );

//       setDailyUsers(res.data);
//     } catch (err) {
//       console.error("Daily users error:", err);
//     }
//   };

//   /* ================= NEW: TIME SPENT ================= */
//   const fetchTimeSpent = async () => {
//     try {
//       const res = await axios.get(
//         `${BASE_URL}/api/admin/analytics/time-spent`,
//         { headers: getHeaders() },
//       );

//       setTimeSpent(res.data);
//     } catch (err) {
//       console.error("Time spent error:", err);
//     }
//   };

//   /* ================= LOAD ALL ANALYTICS ================= */
//   const loadAnalytics = async () => {
//     try {
//       setLoading(true);

//       const results = await Promise.allSettled([
//         fetchTotalUsers(),
//         fetchActiveUsers(),
//         fetchDailyUsers(),
//         fetchTimeSpent(),
//       ]);

//       results.forEach((result, index) => {
//         if (result.status === "rejected") {
//           console.error(`API ${index} failed:`, result.reason);
//         }
//       });
//     } catch (err) {
//       console.error("Analytics load error:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <AdminContext.Provider
//       value={{
//         // 🔥 EXISTING
//         sessions,
//         fetchAllSessions,

//         // 🔥 NEW
//         totalUsers,
//         activeUsers,
//         dailyUsers,
//         timeSpent,
//         fetchTotalUsers,
//         fetchActiveUsers,
//         fetchDailyUsers,
//         fetchTimeSpent,
//         loadAnalytics,

//         // 🔥 COMMON
//         loading,
//         error,
//       }}
//     >
//       {children}
//     </AdminContext.Provider>
//   );
// };
export const useAdmin = () => useContext(AdminContext);
